/**
 * useBatchRunner — Serial batch orchestrator for a user-selected sub-flow.
 *
 * Runs the workflow SUBSET (between `startNodeId` INICIO and `endNodeId` FIN)
 * once per source, in strict serial. Each iteration:
 *   1. Overrides the start upload node's outputUrl to the item's source.
 *   2. Sets the batch context (batch_id + batch_item_index) so every
 *      POST /api/executions tags itself with those fields.
 *   3. Awaits `runPartialWorkflowAndWait(processQueue)`.
 *   4. Captures each involved process node's `outputUrl` into item.nodeOutputs.
 *   5. Records the end node's output as `finalOutputUrl`.
 *   6. Marks the item completed/failed, advances to the next source.
 *
 * Errors that look global (auth / credit exhaustion / network) abort the
 * whole batch; per-item errors fail that item and continue.
 */
import { useCallback } from 'react';
import { useWorkflowStore } from '../store/workflowStore';
import { useBatchStore } from '../store/batchStore';

const uuid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `batch_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const GLOBAL_ERROR_RE =
  /(insufic|unauthor|401|402|forbidden|403|network|econnrefused)/i;

export const useBatchRunner = () => {
  const runBatch = useCallback(async () => {
    const ws = useWorkflowStore.getState();
    const bs = useBatchStore.getState();

    // Re-validate the flow one last time so we bail early on stale state.
    const flow = bs.recomputeFlow();
    if (!flow.valid) {
      throw new Error(flow.error || 'Flujo del lote inválido.');
    }
    if (!Array.isArray(bs.sources) || bs.sources.length === 0) {
      throw new Error('No hay fuentes cargadas para el lote.');
    }
    if (ws.isRunningWorkflow) {
      throw new Error('Ya hay un flujo en ejecución. Esperá a que termine.');
    }
    const startNode = ws.nodes.find((n) => n.id === bs.startNodeId);
    if (!startNode || startNode.type !== 'uploadImage') {
      throw new Error('El nodo INICIO ya no es un Upload Image válido.');
    }

    const batchId = uuid();
    const items = bs.sources.map((src, index) => ({
      index,
      sourceUrl: src.url,
      sourceLabel: src.name || `item_${index + 1}`,
      status: 'queued',
      currentNodeId: null,
      nodeOutputs: {},
      executionIds: [],
      finalOutputUrl: null,
      finalNodeId: bs.endNodeId,
      error: null,
      startedAt: null,
      finishedAt: null,
    }));
    useBatchStore.getState().beginBatch({ batchId, items });

    // Snapshot original state of the start upload node so we can restore
    // the canvas visual after the batch (best-effort).
    const originalUploadData = { ...(startNode.data || {}) };

    try {
      for (let i = 0; i < items.length; i++) {
        // Honor cooperative cancel between items.
        if (useBatchStore.getState().status === 'cancelled') break;

        const item = items[i];
        useBatchStore.getState().setCurrentIndex(i);
        useBatchStore.getState().updateItem(i, {
          status: 'running',
          startedAt: new Date().toISOString(),
        });

        // Inject the item's source URL into the start upload node.
        // `file: null` is critical — clears any stale local File object left
        // over from the node's original manual upload (used just to make the
        // edge connectable). Without it, downstream nodes that prefer `file`
        // over `outputUrl` (e.g. VideoGenerationNode) would keep reusing that
        // first image for every item in the batch.
        useWorkflowStore.getState().updateNodeData(bs.startNodeId, {
          hasOutput: true,
          outputUrl: item.sourceUrl,
          preview: item.sourceUrl,
          fileName: item.sourceLabel,
          file: null,
          fromAsset: true,
          type: 'gallery',
          uploading: false,
          uploaded: true,
          _batchOverride: true,
        });

        useBatchStore.getState().setActiveContext({
          batch_id: batchId,
          batch_item_index: i,
        });

        // Let React propagate the updated node data downstream.
        await new Promise((r) => setTimeout(r, 60));

        try {
          await useWorkflowStore
            .getState()
            .runPartialWorkflowAndWait(bs.processQueue);

          // Capture outputs of all involved process nodes into the item.
          const finalState = useWorkflowStore.getState();
          const nodeOutputs = {};
          for (const nodeId of bs.processQueue) {
            const n = finalState.nodes.find((x) => x.id === nodeId);
            const out = n?.data?.outputUrl;
            if (out && typeof out === 'string') nodeOutputs[nodeId] = out;
          }
          const finalOutputUrl = nodeOutputs[bs.endNodeId] || null;

          useBatchStore.getState().updateItem(i, {
            status: finalOutputUrl ? 'completed' : 'failed',
            nodeOutputs,
            finalOutputUrl,
            error: finalOutputUrl ? null : 'El nodo FIN no produjo salida.',
            finishedAt: new Date().toISOString(),
          });
        } catch (itemErr) {
          const msg = itemErr?.message || String(itemErr);
          useBatchStore.getState().updateItem(i, {
            status: 'failed',
            error: msg,
            finishedAt: new Date().toISOString(),
          });
          if (GLOBAL_ERROR_RE.test(msg)) {
            useBatchStore.getState().endBatch({ status: 'failed', error: msg });
            return;
          }
        }
      }
      useBatchStore.getState().endBatch({ status: 'completed' });
    } finally {
      useBatchStore.getState().setActiveContext(null);
      try {
        useWorkflowStore.getState().updateNodeData(bs.startNodeId, originalUploadData);
      } catch (_) { /* node was deleted mid-batch */ }
    }
  }, []);

  const cancelBatch = useCallback(() => {
    useBatchStore.getState().cancelBatch();
    useWorkflowStore.getState().cancelWorkflow();
  }, []);

  /**
   * Retry only the failed items of the last run. Keeps successful items
   * intact and re-runs the failed ones in place. If everything succeeded
   * or nothing ran, this is a no-op.
   */
  const retryFailedItems = useCallback(async () => {
    const bs = useBatchStore.getState();
    const ws = useWorkflowStore.getState();

    const flow = bs.recomputeFlow();
    if (!flow.valid) throw new Error(flow.error || 'Flujo del lote inválido.');
    if (ws.isRunningWorkflow) throw new Error('Ya hay un flujo en ejecución.');
    if (bs.status === 'running') throw new Error('El lote ya se está ejecutando.');

    const failedIndexes = bs.items
      .map((it, i) => ({ it, i }))
      .filter(({ it }) => it.status === 'failed')
      .map(({ i }) => i);

    if (failedIndexes.length === 0) {
      throw new Error('No hay items fallidos para reintentar.');
    }

    const startNode = ws.nodes.find((n) => n.id === bs.startNodeId);
    if (!startNode || startNode.type !== 'uploadImage') {
      throw new Error('El nodo INICIO ya no es un Upload Image válido.');
    }
    const originalUploadData = { ...(startNode.data || {}) };

    // Keep the same batch_id so retried executions still belong to the
    // original batch — makes historial trazable end-to-end.
    const batchId = bs.activeBatchId || (
      (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `batch_${Date.now()}`
    );

    useBatchStore.setState({
      status: 'running',
      error: null,
      activeBatchId: batchId,
      currentIndex: -1,
      startedAt: new Date().toISOString(),
      finishedAt: null,
    });

    try {
      for (const i of failedIndexes) {
        if (useBatchStore.getState().status === 'cancelled') break;

        const item = useBatchStore.getState().items[i];
        // Reset the item's runtime state before retrying
        useBatchStore.getState().updateItem(i, {
          status: 'running',
          nodeOutputs: {},
          finalOutputUrl: null,
          error: null,
          startedAt: new Date().toISOString(),
          finishedAt: null,
        });
        useBatchStore.getState().setCurrentIndex(i);

        useWorkflowStore.getState().updateNodeData(bs.startNodeId, {
          hasOutput: true,
          outputUrl: item.sourceUrl,
          preview: item.sourceUrl,
          fileName: item.sourceLabel,
          file: null,
          fromAsset: true,
          type: 'gallery',
          uploading: false,
          uploaded: true,
          _batchOverride: true,
        });

        useBatchStore.getState().setActiveContext({
          batch_id: batchId,
          batch_item_index: i,
          retry: true,
        });

        await new Promise((r) => setTimeout(r, 60));

        try {
          await useWorkflowStore
            .getState()
            .runPartialWorkflowAndWait(bs.processQueue);

          const finalState = useWorkflowStore.getState();
          const nodeOutputs = {};
          for (const nodeId of bs.processQueue) {
            const n = finalState.nodes.find((x) => x.id === nodeId);
            const out = n?.data?.outputUrl;
            if (out && typeof out === 'string') nodeOutputs[nodeId] = out;
          }
          const finalOutputUrl = nodeOutputs[bs.endNodeId] || null;

          useBatchStore.getState().updateItem(i, {
            status: finalOutputUrl ? 'completed' : 'failed',
            nodeOutputs,
            finalOutputUrl,
            error: finalOutputUrl ? null : 'El nodo FIN no produjo salida.',
            finishedAt: new Date().toISOString(),
          });
        } catch (itemErr) {
          const msg = itemErr?.message || String(itemErr);
          useBatchStore.getState().updateItem(i, {
            status: 'failed',
            error: msg,
            finishedAt: new Date().toISOString(),
          });
          if (GLOBAL_ERROR_RE.test(msg)) {
            useBatchStore.getState().endBatch({ status: 'failed', error: msg });
            return;
          }
        }
      }
      useBatchStore.getState().endBatch({ status: 'completed' });
    } finally {
      useBatchStore.getState().setActiveContext(null);
      try {
        useWorkflowStore.getState().updateNodeData(bs.startNodeId, originalUploadData);
      } catch (_) { /* deleted mid-batch */ }
    }
  }, []);

  return { runBatch, cancelBatch, retryFailedItems };
};

export default useBatchRunner;
